import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { useIsMutating } from '@tanstack/react-query';
import { ConfirmDialog } from '@bliks-io/ui';
import { useDataSyncStatus } from '@/api/queries/useDataSync';
import { useReadPersonioForSetup, useUnlinkPersonio } from '@/api/mutations/useDataSync';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { dataSyncKeys } from '@/api/query-keys';
import { PersonioLinkDialog } from '@/pages/organization-settings/data-sync/PersonioLinkDialog';
import { TableToolbarButton } from './components/TableToolbarButton';

/**
 * Personio for the wizard: link (the read follows), read, unlink after a
 * confirm. The read only refreshes the setup rows; the steps seed from them
 * (useSeedOnOpen); `onLoaded` tells the seed ledger which step loaded, also
 * when the admin left that step before the read finished. Owned by the step,
 * not a button: the read moves from the empty state to the table toolbar
 * while it runs.
 */
export function usePersonioImport({ onLoaded }: { onLoaded?: () => void } = {}) {
  const { t } = useTranslation();
  const { data: status } = useDataSyncStatus();
  const readPersonio = useReadPersonioForSetup({ onSuccess: onLoaded });
  const unlinkPersonio = useUnlinkPersonio();
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [isUnlinkOpen, setIsUnlinkOpen] = useState(false);
  const isLinked = !!status?.personio;
  // Busy from the cache, not this observer: a step opened again mid-read must
  // not start a second full read.
  const isLoading = useIsMutating({ mutationKey: dataSyncKeys.readPersonioForSetup() }) > 0;
  const load = () => readPersonio.mutate();

  return {
    isLinked,
    isLoading,
    load,
    connect: () => setIsLinkOpen(true),
    unlink: () => setIsUnlinkOpen(true),
    isUnlinking: unlinkPersonio.isPending,
    // Only a linked Personio reads; the read merges into the table as it is.
    toolbarButton: isLinked && (
      <TableToolbarButton loading={isLoading} onClick={load}>
        <RefreshCw className="size-3.5!" />
        {t('initialSetup.personio.load')}
      </TableToolbarButton>
    ),
    dialogs: (
      <>
        <PersonioLinkDialog open={isLinkOpen} onOpenChange={setIsLinkOpen} onLinked={load} />
        <ConfirmDialog
          open={isUnlinkOpen}
          onOpenChange={setIsUnlinkOpen}
          variant="danger"
          title={t('dataSync.settings.unlinkConfirm.title')}
          description={t('initialSetup.personio.unlinkDescription')}
          confirmLabel={t('dataSync.settings.unlink')}
          onConfirm={() =>
            unlinkPersonio.mutate(undefined, {
              onSuccess: () => setIsUnlinkOpen(false),
              onError: (error) => toastApiError(error, 'errors.unknown')
            })
          }
          isPending={unlinkPersonio.isPending}
        />
      </>
    )
  };
}
